import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '@/lib/cn';

export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'font-display font-bold leading-none',
    'transition-[transform,box-shadow,background-color,color] duration-200 ease-[var(--ease-out-soft)]',
    'disabled:pointer-events-none disabled:opacity-50',
    'active:scale-[0.97]',
    '[&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        /** Main site action — "Adicionar ao carrinho", "Ver catálogo". */
        primary:
          'bg-orange-500 text-white shadow-card hover:bg-orange-600 hover:shadow-card-hover hover:-translate-y-0.5',
        /** Reserved for anything that opens WhatsApp. Never used otherwise. */
        whatsapp:
          'bg-whats-500 text-white shadow-card hover:bg-whats-600 hover:shadow-card-hover hover:-translate-y-0.5',
        /** High-contrast action on cream backgrounds. */
        dark: 'bg-brown-900 text-cream-100 shadow-card hover:bg-brown-800 hover:shadow-card-hover hover:-translate-y-0.5',
        outline:
          'border-2 border-sand-300 bg-transparent text-brown-700 hover:border-caramel-500 hover:bg-cream-200 hover:text-brown-900',
        soft: 'bg-cream-300 text-brown-800 hover:bg-sand-200',
        ghost: 'bg-transparent text-brown-700 hover:bg-cream-300 hover:text-brown-900',
        /** For dark sections, where cream reads as the "filled" state. */
        inverse: 'bg-cream-100 text-brown-900 shadow-card hover:bg-white hover:-translate-y-0.5',
        danger: 'bg-danger-500 text-white hover:bg-red-700',
      },
      size: {
        /** 44px — the minimum comfortable touch target. */
        sm: 'h-11 rounded-xl px-4 text-sm [&_svg]:size-4',
        md: 'h-13 rounded-xl px-6 text-sm [&_svg]:size-5',
        lg: 'h-14 rounded-2xl px-8 text-base [&_svg]:size-5',
        icon: 'size-11 rounded-xl [&_svg]:size-5',
        'icon-sm': 'size-9 rounded-lg [&_svg]:size-4',
      },
      full: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Render the child element instead of a `<button>` — for Link and anchor. */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, full, asChild = false, type, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref}
      // Buttons inside forms default to submit; that is almost never intended.
      type={asChild ? undefined : (type ?? 'button')}
      className={cn(buttonVariants({ variant, size, full }), className)}
      {...props}
    />
  );
});
